/*
Government Purpose Rights (“GPR”)
Contract No.  W911NF-14-D-0005
Contractor Name:   University of Southern California
Contractor Address:  3720 S. Flower Street, 3rd Floor, Los Angeles, CA 90089-0001
Expiration Date:  Restrictions do not expire, GPR is perpetual
Restrictions Notice/Marking: The Government's rights to use, modify, reproduce, release, perform, display, or disclose this software are restricted by paragraph (b)(2) of the Rights in Noncommercial Computer Software and Noncommercial Computer Software Documentation clause contained in the above identified contract.  No restrictions apply after the expiration date shown above. Any reproduction of the software or portions thereof marked with this legend must also reproduce the markings. (see: DFARS 252.227-7014(f)(2)) 
No Commercial Use: This software shall be used for government purposes only and shall not, without the express written permission of the party whose name appears in the restrictive legend, be used, modified, reproduced, released, performed, or displayed for any commercial purpose or disclosed to a person other than subcontractors, suppliers, or prospective subcontractors or suppliers, who require the software to submit offers for, or perform, government contracts.  Prior to disclosing the software, the Contractor shall require the persons to whom disclosure will be made to complete and sign the non-disclosure agreement at 227.7103-7.  (see DFARS 252.227-7025(b)(2))
*/
import { v4 as uuid } from "uuid";
import {
  DialogueMessage,
  DialogueMessageType,
} from "@ictlearningsciences/pal3-core/types";
import {
  getRandomScriptByType,
  getScriptByText,
} from "@ictlearningsciences/pal3-core/dialogue";
import {
  Animation,
  AnimationType,
  GetAnimationByType,
} from "@ictlearningsciences/pal3-core/character";
import {
  addMessagesToQueue,
  setCurMessage,
  removeMessageFromQueue,
  clearMessageQueue,
  setAnimation,
  setDockOpen,
  messagePlayed,
  IdDialogueMessage,
  play,
} from "../store/slices/dialogue";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { useWithSettings } from "./useWithSettings";

export interface UseWithDialogue {
  getRandomMessage: (type: DialogueMessageType) => DialogueMessage;
  getMessage: (text: string) => DialogueMessage;
  addMessage: (msg: DialogueMessage, id?: string) => void; // add message to queue
  addMessages: (msgs: DialogueMessage[]) => void; // add messages to queue
  playMessage: (msg: DialogueMessage, id?: string) => void; // play message (ignore queue)
  nextMessage: () => void; // play next message in queue
  clearMessages: () => void;
  playAnimation: (anim: Animation | AnimationType) => void;
  poke: () => void;
  openDock: (tf?: boolean) => void;
  messageFinishedPlaying: (message: IdDialogueMessage) => void;
}

export function useWithDialogue(): UseWithDialogue {
  const dispatch = useAppDispatch();
  const { activeGoal } = useAppSelector((state) => state.userData);
  const { dialogueMessages } = useAppSelector((state) => state.data);
  const { isDockOpen, messages, curMessage, messagesPlayed } = useAppSelector(
    (state) => state.dialogue
  );
  const { character, audioMode, volume, voiceTTS } = useAppSelector(
    (state) => state.settings
  );
  const { getVoices } = useWithSettings();

  function getRandomMessage(type: DialogueMessageType) {
    return getRandomScriptByType(type, dialogueMessages);
  }

  function getMessage(text: string) {
    const msg = getScriptByText(text, dialogueMessages);
    if (!msg) {
      return { text: text };
    }
    return msg;
  }

  function addMessage(msg: DialogueMessage, id?: string): void {
    dispatch(addMessagesToQueue([{ ...msg, id: id || uuid() }]));
  }

  function addMessages(msgs: DialogueMessage[]): void {
    dispatch(addMessagesToQueue(msgs.map((m) => ({ ...m, id: uuid() }))));
  }

  function nextMessage(): void {
    if (messages.length === 0) {
      if (curMessage) {
        dispatch(messagePlayed(curMessage));
        dispatch(setCurMessage(undefined));
      } else if (messagesPlayed.length > 0) {
        playMessage(messagesPlayed[messagesPlayed.length - 1]);
      }
    } else {
      playMessage(messages[0]);
      dispatch(removeMessageFromQueue());
    }
  }

  function playMessage(msg: DialogueMessage, id?: string): void {
    if (curMessage && curMessage.text === msg.text) return;
    const audio = getScriptByText(msg.text, dialogueMessages);
    let voice;
    if (voiceTTS) {
      voice =
        speechSynthesis.getVoices().find((v) => v.name === voiceTTS) || null;
    } else {
      const voices = getVoices().filter((v) => v.type === "tts");
      voice =
        speechSynthesis.getVoices().find((v) => v.name === voices[0]?.id) ||
        null;
    }
    dispatch(
      play({
        msg: {
          ...msg,
          id: id || uuid(),
          audioFile: msg?.audioFile || audio?.audioFile,
        },
        character,
        audioMode,
        volume: activeGoal?.alias.startsWith("sa-bystander-training")
          ? 0
          : volume,
        voice,
      })
    );
  }

  function clearMessages(): void {
    playAnimation(AnimationType.idle);
    dispatch(clearMessageQueue());
  }

  function poke(): void {
    playMessage(
      getRandomScriptByType(DialogueMessageType.Poke, dialogueMessages)
    );
  }

  function openDock(tf?: boolean): void {
    dispatch(setDockOpen(tf === undefined ? !isDockOpen : tf));
  }

  function playAnimation(anim: Animation | AnimationType): void {
    if (typeof anim === "string") {
      const animation = GetAnimationByType(character, anim);
      if (animation) {
        dispatch(setAnimation(animation));
      }
    } else {
      dispatch(setAnimation(anim));
    }
  }

  function messageFinishedPlaying(message: IdDialogueMessage) {
    dispatch(messagePlayed(message));
  }

  return {
    getMessage,
    getRandomMessage,
    addMessage,
    addMessages,
    nextMessage,
    playMessage,
    clearMessages,
    playAnimation,
    poke,
    openDock,
    messageFinishedPlaying,
  };
}
